import { useEffect, useReducer } from 'react';
import {
    TyrePageErrors,
    TyrePageState,
    TyreProcessStages,
    TyreRepairRequest,
    ReducerActionNames,
    nonUSTyreDamageTypes
} from '../sitePages/tyreRepairRequest/TyreRepairRequestTypes';
import TyreRepairRequestReducer from '../sitePages/tyreRepairRequest/TyreRepairRequestReducer';
import {
    Locations,
    ProductTypes,
    RoutePaths,
    UploadImageTypes
} from '../../Constants/Common';
import { useNavigate } from 'react-router-dom';
import { RepairRequestApi } from '../../api/RepairRequestApi';
import { useAuth0 } from '@auth0/auth0-react';
import { ClientDateHandler } from '../common/clientDateHandler/ClientDateHandler';
import { CommonValidation } from '../common/commonValidation/commonValidation';
import { PolicyHolderApi } from '../../api/PolicyHolderApi';
import {
    AddressType,
    DealerAddressType,
    PolicyHolderType,
    ProductType,
    UploadedDocument,
    VehicleType
} from '../common/CommonTypes';
import { ProgressBarItem } from '../common/progressBar/ProgressBar';
import { TyreRepairRequestContextState } from '../../context/TyreRepairRequestContext';
import { GoogleAnalytics } from '../../classes/GoogleAnalytics';
import { ConfirmationLocationState } from '../sitePages/ConfirmationPage';
import { useTranslation } from 'react-i18next';
import { SelectInputHelper } from '../common/selectInput/SelectInput';
import { useAppSelector } from 'state/hooks';

function useTyreRepairRequest(
    initialState: TyrePageState,
    product: ProductType,
    vehicle: VehicleType
): TyreRepairRequestContextState {
    const { user, getAccessTokenSilently } = useAuth0();
    const navigate = useNavigate();

    const showRepairAddress = useAppSelector((s) => s.site.showRepairAddress);

    const [tyreRepairRequest, dispatch] = useReducer(
        TyreRepairRequestReducer,
        initialState
    );

    const { t, i18n } = useTranslation();
    const isSiteLocationUs = i18n.language?.toLowerCase() === Locations.Us;
    const isSiteLocationUk = i18n.language?.toLowerCase() === Locations.Uk;

    useEffect(() => {
        const getPolicyHolderDetails = async () => {
            const token = await getAccessTokenSilently();

            new PolicyHolderApi().getPolicyHolderDetails(
                product.reference,
                token,
                onGetPolicyHolderSuccess,
                onGetPolicyHolderFailure
            );
        };

        if (!isSiteLocationUs) {
            getPolicyHolderDetails();
        }
    }, []);

    const onGetPolicyHolderSuccess = (
        policyHolderDetails: PolicyHolderType
    ) => {
        dispatch({
            type: ReducerActionNames.SetPolicyData,
            address: policyHolderDetails.address
        });
    };

    const onGetPolicyHolderFailure = (error: string) => {
        console.log(`Failed to get the policy holder details: ${error}`);
    };

    const {
        tyreStage,
        tyreType,
        mileage,
        permissionGranted,
        tyreRepairRequests,
        currentRepairRequest,
        repairAddresses,
        repairAddressSelectedIndex,
        updateTyre,
        scrollToAddress,
        dateOfIncident
    } = tyreRepairRequest;

    const progressBarItems: ProgressBarItem[] = [
        {
            order: 1,
            text: t('tyre_repair_request.progress_bar.text.general'),
            active: tyreStage === TyreProcessStages.General,
            disabled: false,
            visible: true
        },
        {
            order: 2,
            text: t('tyre_repair_request.progress_bar.text.tyre_selection'),
            active: tyreStage === TyreProcessStages.TyreSelection,
            disabled: false,
            visible: true
        },
        {
            order: 3,
            text: t('tyre_repair_request.progress_bar.text.summary'),
            active: tyreStage === TyreProcessStages.Summary,
            disabled: false,
            visible: true
        },
        {
            order: 4,
            text: t('tyre_repair_request.progress_bar.text.confirmation'),
            active: false,
            disabled: false,
            visible: true
        }
    ];

    const onSelectTyreType = (selectionName: string) => {
        dispatch({
            type: ReducerActionNames.SelectTyreType,
            tyreType: selectionName
        });
    };

    const onChangeMileage = (value: string): void => {
        dispatch({
            type: ReducerActionNames.UpdateMileage,
            mileage: value
        });
    };

    const onChangDateOfIncident = (value: string): void => {
        dispatch({
            type: ReducerActionNames.UpdateIncidentDate,
            dateOfIncident: value
        });
    };

    const onTyreValueChange = (key: keyof TyreRepairRequest, value: string) => {
        dispatch({
            type: ReducerActionNames.UpdateTyreRepairRequest,
            key,
            value
        });
    };

    const onTyreRepairRequestChangeTyre = (
        value: string,
        key: keyof TyreRepairRequest
    ): void => {
        dispatch({
            type: ReducerActionNames.UpdateTyreRepairRequest,
            key,
            value
        });
    };

    const onTyreSelected = (tyre: string) => {
        dispatch({
            type: ReducerActionNames.SetTyre,
            tyre,
            productReference: product.reference
        });
    };

    const onChangeDamageType = (selectionName: string) => {
        dispatch({
            type: ReducerActionNames.UpdateTyreRepairRequest,
            key: 'damageType',
            value: selectionName
        });
    };

    const onPoliceReportChange = (report: UploadedDocument) => {
        dispatch({
            type: ReducerActionNames.UpdatePoliceReport,
            report
        });
    };

    const onRepairAddressSelectionChanged = (selectedIndex: number) => {
        dispatch({
            type: ReducerActionNames.UpdateRepairAddressSelected,
            value: selectedIndex
        });
    };

    const onDealerAddressUpdate = (dealerAddress: DealerAddressType) => {
        dispatch({
            type: ReducerActionNames.UpdateDealerAddress,
            address: dealerAddress
        });
    };

    const onDefaultDealerAddressUpdate = (
        defaultDealerAddress: AddressType
    ) => {
        dispatch({
            type: ReducerActionNames.SetPolicyData,
            address: defaultDealerAddress
        });
    };

    const onUploadImageChange = (
        imageType: string,
        uploadedPath: string,
        imageId: string,
        image: string,
        filename: string
    ) => {
        const uploadedImages = currentRepairRequest.uploadedImages;

        const uploadedImage = uploadedImages.find((ui) => ui.type == imageType);

        if (uploadedImage) {
            uploadedImage.uploadedPath = uploadedPath;
            uploadedImage.image = CommonValidation.isNotEmpty(uploadedPath)
                ? image
                : undefined;
            uploadedImage.filename = CommonValidation.isNotEmpty(uploadedPath)
                ? filename
                : undefined;
            uploadedImage.imageId = imageId;

            dispatch({
                type: ReducerActionNames.UpdateUploadedImages,
                uploadedImages: uploadedImages
            });
        }
    };

    const onTogglePermission = (isChecked: boolean) => {
        dispatch({
            type: ReducerActionNames.SetPermissionGranted,
            permissionGranted: isChecked
        });
    };

    const onChangeStage = (stage: TyreProcessStages): void => {
        dispatch({
            type: ReducerActionNames.UpdateTyreStage,
            newStage: stage
        });

        if (CommonValidation.isNotEmpty(updateTyre)) {
            onUpdateTyreClick('');
        }

        if (scrollToAddress) {
            onUpdateAddressClick(false);
        }
    };

    const onFinishedTyreRepairRequest = () => {
        const validPage = validateRepairPage();

        if (validPage) {
            saveRepairRequest();
        }
    };

    const validateRepairPage = () => {
        let errors: TyrePageErrors = {};
        let isValid = true;

        if (!permissionGranted) {
            isValid = false;
            errors = {
                ...errors,
                permissionGranted: t('validate_agreement')
            };
        }

        dispatch({
            type: ReducerActionNames.UpdateTyreErrors,
            tyreErrors: errors
        });

        console.log(isValid);

        return isValid;
    };

    const validateGeneralPage = () => {
        let errors: TyrePageErrors = {};
        let isValid = true;

        if (CommonValidation.isEmpty(mileage)) {
            isValid = false;
            errors = {
                ...errors,
                mileage: t('approximate_mileage_is_required')
            };
        }
        if (!CommonValidation.isValidNumeric(mileage)) {
            isValid = false;
            errors = {
                ...errors,
                mileage: t('approx_mileage_is_number')
            };
        }

        if (
            (dateOfIncident &&
                CommonValidation.isDateNull(dateOfIncident, true)) ||
            (dateOfIncident && CommonValidation.isEmpty(dateOfIncident))
        ) {
            isValid = false;
            errors = {
                ...errors,
                dateOfIncident: t('date_of_incident_is_required')
            };
        }

        if (showRepairAddress && repairAddressSelectedIndex < 0) {
            isValid = false;
            errors = {
                ...errors,
                noRepairAddressSelected: t('select_repair_address')
            };
        }

        dispatch({
            type: ReducerActionNames.UpdateTyreErrors,
            tyreErrors: errors
        });

        return isValid;
    };

    const saveRepairRequest = async () => {
        const successCallback = (repairRequests) => {
            const activeConfirmationItems = progressBarItems.map((p) =>
                p.text === 'Confirmation'
                    ? { ...p, active: true }
                    : { ...p, active: false }
            );

            const state: ConfirmationLocationState = {
                progressBarItems: activeConfirmationItems,
                repairRequests,
                productType: ProductTypes.Tyre
            };

            navigate(RoutePaths.Confirmation, { state });
        };

        const failureCallback = (error) => {
            console.log(`Failed to register policy: ${error}`);

            navigate(RoutePaths.ConfirmationError);
        };

        dispatch({
            type: ReducerActionNames.LoadingOnRequestSubmission,
            loadingOnRequestSubmission: true
        });

        const repairRequests = tyreRepairRequests.map((request) =>
            getRepairRequest(request)
        );

        console.log(`Repair requests: ${JSON.stringify(repairRequests)}`);

        const token = await getAccessTokenSilently();

        new RepairRequestApi().saveTyreRepair(
            repairRequests,
            token,
            successCallback,
            failureCallback
        );
    };

    const getRepairRequest = (request: TyreRepairRequest) => {
        const damageTypeComment =
            request.damageType === nonUSTyreDamageTypes.Other
                ? {
                      damageTypeComment: request.damageTypeComment
                  }
                : {};

        const repairAddressToSend = showRepairAddress
            ? repairAddresses[repairAddressSelectedIndex] || {}
            : {};

        const repairRequest = {
            submitterEmailAddress: user.email,
            productPolicyId: request.productReference,
            tyreType: tyreType,
            mileage: mileage,
            damagedTyre: request.damagedTyre,
            dateOfIncident: mapJsonDateToServer(dateOfIncident),
            damageType: request.damageType,
            ...damageTypeComment,
            damageSize: request.damageSize,
            additionalInformation: request.additionalInformation,
            policeReportGuid: request?.policeReport?.uploadedFileId,
            closeUpImageGuid: request.uploadedImages.find(
                (ui) => ui.type === UploadImageTypes.CloseUp
            ).imageId,
            distanceImageGuid: request.uploadedImages.find(
                (ui) => ui.type === UploadImageTypes.Distance
            ).imageId,
            additionalImage1Guid:
                request.uploadedImages.find(
                    (ui) => ui.type === UploadImageTypes.AdditionalImage1
                ).imageId || '00000000-0000-0000-0000-000000000000',
            additionalImage2Guid:
                request.uploadedImages.find(
                    (ui) => ui.type === UploadImageTypes.AdditionalImage2
                ).imageId || '00000000-0000-0000-0000-000000000000',
            repairAddress: repairAddressToSend,
            countryIso: i18n.language.toLowerCase(),
            repairOption:
                request.repairOption === '(null)' ? '' : request.repairOption,
            crimeReferenceNumber: request.crimeReferenceNumber || ''
        };

        return repairRequest;
    };

    const mapJsonDateToServer = (dateJson: string) => {
        const cdh = new ClientDateHandler();
        cdh.parseJSON(dateJson);

        const localDate = cdh.getLocalDate();
        return localDate;
    };

    const onUpdateTyreClick = (tyre: string): void => {
        dispatch({
            type: ReducerActionNames.UpdateTyre,
            updateTyre: tyre
        });
    };

    const onUpdateAddressClick = (scrollToAddress): void => {
        dispatch({
            type: ReducerActionNames.ScrollToAddress,
            scrollToAddress: scrollToAddress
        });
    };

    const onCancelClick = () => {
        dispatch({
            type: ReducerActionNames.ShowCancelAlert,
            showCancelAlert: true
        });
    };

    const onCancelRepairRequest = () => {
        GoogleAnalytics.trackEventsGoogleAnalytics(
            'Button click',
            'Tyre button clicks',
            'Clicked cancel repair request'
        );
        navigate(RoutePaths.Home);
    };

    const onContinueRepairRequest = (): void => {
        if (tyreStage === TyreProcessStages.General && validateGeneralPage())
            onChangeStage(TyreProcessStages.TyreSelection);
    };

    const onCancelUndo = () => {
        dispatch({
            type: ReducerActionNames.ShowCancelAlert,
            showCancelAlert: false
        });
    };

    const onContinueToRepairAddress = (): void => {
        onChangeStage(TyreProcessStages.Summary);
    };

    const onFinishAddingTyres = (): boolean => {
        const formWasValid = validateCurrentTyreRepairRequest();

        if (formWasValid) {
            dispatch({ type: ReducerActionNames.AddTyreRequest });
            onChangeStage(TyreProcessStages.TyreSelection);
        }
        dispatch({
            type: ReducerActionNames.SetHasErrors,
            hasErrors: !formWasValid
        });

        return formWasValid;
    };

    const validateCurrentTyreRepairRequest = () => {
        let errors: TyrePageErrors = {};
        let isValid = true;
        if (
            CommonValidation.isDateNull(
                currentRepairRequest.dateOfIncident,
                true
            ) ||
            CommonValidation.isEmpty(currentRepairRequest.dateOfIncident)
        ) {
            isValid = false;
            errors = {
                ...errors,
                tyreSpecifics: {
                    ...errors.tyreSpecifics,
                    dateOfIncident: t('date_of_incident_is_required')
                }
            };
        } else if (
            !CommonValidation.isValidDate(
                currentRepairRequest.dateOfIncident,
                true
            )
        ) {
            isValid = false;
            errors = {
                ...errors,
                tyreSpecifics: {
                    ...errors.tyreSpecifics,
                    dateOfIncident: 'Date of incident is out of a valid range'
                }
            };
        }
        if (SelectInputHelper.isNullValue(currentRepairRequest.damageType)) {
            isValid = false;
            errors = {
                ...errors,
                tyreSpecifics: {
                    ...errors.tyreSpecifics,
                    damageType: t('type_of_damage_is_required')
                }
            };
        }
        if (
            currentRepairRequest.damageType === nonUSTyreDamageTypes.Other &&
            SelectInputHelper.isNullOrEmptyalue(
                currentRepairRequest.damageTypeComment
            )
        ) {
            isValid = false;
            errors = {
                ...errors,
                tyreSpecifics: {
                    ...errors.tyreSpecifics,
                    damageTypeComment: t('tyre_damage_comment_is_required')
                }
            };
        }
        if (
            currentRepairRequest.damageType === nonUSTyreDamageTypes.Malicious
        ) {
            if (
                !isSiteLocationUk &&
                SelectInputHelper.isNullValue(currentRepairRequest.policeReport)
            ) {
                isValid = false;
                errors = {
                    ...errors,
                    tyreSpecifics: {
                        ...errors.tyreSpecifics,
                        policeReport: t('police_report_is_required')
                    }
                };
            }
            if (isSiteLocationUk) {
                if (
                    CommonValidation.isEmpty(
                        currentRepairRequest.crimeReferenceNumber
                    )
                ) {
                    isValid = false;
                    errors = {
                        ...errors,
                        tyreSpecifics: {
                            ...errors.tyreSpecifics,
                            crimeReferenceNumber: t(
                                'crime_reference_number_is_required'
                            )
                        }
                    };
                } else if (
                    !CommonValidation.isValidAlphaNumeric(
                        currentRepairRequest.crimeReferenceNumber
                    )
                ) {
                    isValid = false;
                    errors = {
                        ...errors,
                        tyreSpecifics: {
                            ...errors.tyreSpecifics,
                            crimeReferenceNumber: t(
                                'crime_reference_number_invalid'
                            )
                        }
                    };
                }
            }
        }
        if (
            !isSiteLocationUs &&
            CommonValidation.isEmpty(
                currentRepairRequest.uploadedImages.find(
                    (ui) => ui.type == UploadImageTypes.CloseUp
                )?.uploadedPath
            )
        ) {
            isValid = false;
            errors = {
                ...errors,
                tyreSpecifics: {
                    ...errors.tyreSpecifics,
                    closeUpImage: t('close_up_image_is_required')
                }
            };
        }

        if (
            !isSiteLocationUs &&
            CommonValidation.isEmpty(
                currentRepairRequest.uploadedImages.find(
                    (ui) => ui.type === UploadImageTypes.Distance
                )?.uploadedPath
            )
        ) {
            isValid = false;
            errors = {
                ...errors,
                tyreSpecifics: {
                    ...errors.tyreSpecifics,
                    distanceImage: t('image_from_1_metre_away_is_required')
                }
            };
        }

        dispatch({
            type: ReducerActionNames.UpdateTyreErrors,
            tyreErrors: errors
        });

        return isValid;
    };

    const onDeleteRepairRequest = (tyre: string) => {
        dispatch({
            type: ReducerActionNames.RemoveTyre,
            tyre: tyre,
            newStage: TyreProcessStages.TyreSelection
        });
    };

    return {
        product,
        vehicle,
        progressBarItems,
        tyreRepairRequest,
        onSelectTyreType,
        onChangeMileage,
        onTyreValueChange,
        onTyreRepairRequestChangeTyre,
        onTyreSelected,
        onChangeDamageType,
        onRepairAddressSelectionChanged,
        onDealerAddressUpdate,
        onPoliceReportChange,
        onUploadImageChange,
        onTogglePermission,
        onFinishedTyreRepairRequest,
        onCancelClick,
        onCancelRepairRequest,
        onCancelUndo,
        onChangeStage,
        onUpdateTyreClick,
        onUpdateAddressClick,
        onContinueRepairRequest,
        onContinueToRepairAddress,
        onFinishAddingTyres,
        onDeleteRepairRequest,
        onChangDateOfIncident,
        onDefaultDealerAddressUpdate
    };
}

export default useTyreRepairRequest;
